<template>
  <a-modal
    title="音效文件"
    :visible="visible"
    :confirmLoading="isLoading"
    @ok="touchSubmit"
    @cancel="onClose"
    :destroyOnClose="true"
    width="520px"
  >
    <a-form
      ref="RefForm"
      :model="formState"
      :label-col="{ span: 6 }"
      :wrapper-col="{ span: 16 }"
    >
      <a-form-item
        label="音效文件"
        name="audioFile"
        :rules="[{ required: true, validator: validatorAudioFile }]"
      >
        <Upload
          accept=".wav,.mp3,.aac,.flac,.wma,.aiff,.ogg,.opus"
          uploadSource="SoundEffectConfig"
          :fileNumber="1"
          :fileList="formState.audioFile"
          :uploadBucket="$pub.BUCKET_TIMBRE()"
          :beforeUploadPro="beforeUploadPro"
          :uploadResult="uploadResult"
          :removePro="removePro"
        >
          <template #up-title>上传文件</template>
        </Upload>
        <div class="form-upload-hint">支持 WAV、MP3、AAC、FLAC、WMA、AIFF、OGG、Opus</div>
        <div class="form-upload-list" v-if="formState.audioFile.length">
          <div
            class="form-upload-item"
            v-for="(item, index) in formState.audioFile"
            :key="item.uid || index"
          >
            <span class="form-upload-name">{{ item.name || '音频' }}</span>
            <span v-if="item.status === 'uploading'" class="form-upload-status">上传中...</span>
            <template v-else-if="item.status === 'done'">
              <a class="form-upload-action" @click="touchPlayOrStop(item.url)">{{ playingUrl === item.url ? '停止' : '试听' }}</a>
              <a class="form-upload-action" @click="touchDeleteAudio(index)">删除</a>
            </template>
            <template v-else>
              <a class="form-upload-action" @click="touchDeleteAudio(index)">删除</a>
            </template>
          </div>
        </div>
      </a-form-item>
    </a-form>
  </a-modal>
</template>

<script setup>
import { ref, reactive, nextTick } from 'vue'
import { message } from 'ant-design-vue'
import Upload from '@/components/Upload'
import { sfxDetail, sfxSave } from '@/api/admin'

const emit = defineEmits(['success'])
const RefForm = ref(null)
let visible = ref(false)
let isLoading = ref(false)
let dramaId = ref(null)

let audioInstance = ref(null)
let playingUrl = ref(null)

let formState = reactive({
  url: undefined,
  vid: undefined,
  key: undefined,
  audioFile: []
})

function audioPlayerPlay (url) {
  audioPlayerStop()
  if (!url) return
  const audio = new Audio(url)
  audioInstance.value = audio
  playingUrl.value = url
  audio.play().catch(() => {
    message.warning('播放失败')
    playingUrl.value = null
    audioInstance.value = null
  })
  audio.onended = () => {
    playingUrl.value = null
    audioInstance.value = null
  }
}

function audioPlayerStop () {
  if (audioInstance.value) {
    audioInstance.value.pause()
    audioInstance.value.currentTime = 0
    audioInstance.value = null
  }
  playingUrl.value = null
}

function touchPlayOrStop (url) {
  if (playingUrl.value === url) {
    audioPlayerStop()
  } else {
    audioPlayerPlay(url)
  }
}

function showModal (id) {
  dramaId.value = id
  visible.value = true
  nextTick(() => {
    formState.url = undefined
    formState.vid = undefined
    formState.key = undefined
    formState.audioFile = []
    if (id) {
      sfxDetail({ id }).then(res => {
        if (res.code === 0 && res.data) {
          const { sfx_url } = res.data
          if (sfx_url) {
            formState.url = sfx_url
            formState.audioFile = [{ uid: 'existing', name: '已有音效', status: 'done', url: sfx_url }]
          }
        }
      })
    }
  })
}

function onClose () {
  if (!isLoading.value) {
    audioPlayerStop()
    RefForm.value?.resetFields()
    formState.url = undefined
    formState.vid = undefined
    formState.key = undefined
    formState.audioFile = []
    visible.value = false
  }
}

const AUDIO_EXTS = ['.wav', '.mp3', '.aac', '.flac', '.wma', '.aiff', '.ogg', '.opus']

function validatorAudioFile () {
  if (!formState.url) {
    return Promise.reject(new Error('请上传音效文件'))
  }
  return Promise.resolve()
}

function beforeUploadPro (file) {
  if (formState.audioFile.length >= 1) {
    message.warning('仅支持上传 1 个音效文件')
    return false
  }
  const name = (file.name || '').toLowerCase()
  const hasValidExt = AUDIO_EXTS.some(ext => name.endsWith(ext))
  if (!hasValidExt) {
    message.error('仅支持 WAV、MP3、AAC、FLAC、WMA、AIFF、OGG、Opus 格式')
    return false
  }
  if (file.type && !file.type.startsWith('audio/')) {
    message.error('请上传音频文件')
    return false
  }
  return true
}

function uploadResult (fileJson) {
  if (fileJson.status === 'done') {
    const { url, key, vid } = fileJson
    if (url) {
      formState.url = url
      formState.key = key ?? formState.key
      formState.vid = vid ?? formState.vid
      const idx = formState.audioFile.findIndex(f => f.uid === fileJson.uid)
      const doneItem = { uid: fileJson.uid, name: fileJson.name, status: 'done', url }
      if (idx !== -1) {
        formState.audioFile[idx] = { ...formState.audioFile[idx], url, status: 'done' }
      } else {
        formState.audioFile = [doneItem]
      }
      // 上传成功后重新校验，去掉“请上传 .mp3 音效文件”提示
      nextTick(() => {
        RefForm.value?.validateFields(['audioFile']).catch(() => {})
      })
    }
  }
}

function removePro (file) {
  const idx = formState.audioFile.findIndex(f => f.uid === file.uid)
  if (idx !== -1) {
    const item = formState.audioFile[idx]
    if (item.url === playingUrl.value) audioPlayerStop()
    const hadUrl = item.url === formState.url
    formState.audioFile.splice(idx, 1)
    if (hadUrl) {
      formState.url = undefined
      formState.key = undefined
    }
  }
  return true
}

function touchDeleteAudio (index) {
  const item = formState.audioFile[index]
  if (item && item.url === playingUrl.value) audioPlayerStop()
  const hadUrl = item && item.url === formState.url
  formState.audioFile.splice(index, 1)
  if (hadUrl) {
    formState.url = undefined
    formState.key = undefined
  }
}

function touchSubmit () {
  RefForm.value.validate().then(() => {
    const params = {
      id: dramaId.value,
      sfx_url: formState.url
    }
    isLoading.value = true
    sfxSave(params).then(res => {
      isLoading.value = false
      if (res.code === 0) {
        message.success('操作成功')
        onClose()
        emit('success')
      } else {
        message.error(res.message || '操作失败')
      }
    }).catch(() => {
      isLoading.value = false
    })
  })
}

defineExpose({
  showModal
})
</script>

<style lang="less" scoped>
.form-upload-hint {
  padding-top: 6px;
  color: #8d8d8d;
  font-size: 12px;
}
.form-upload-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;
  &:last-child {
    border-bottom: none;
  }
}
.form-upload-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.form-upload-status {
  color: #1890ff;
  font-size: 12px;
}
.form-upload-action {
  margin-left: 8px;
  font-size: 12px;
}
</style>
